import React, { useState, useEffect } from 'react';
import { X, Save, CheckCircle, AlertCircle, Building2 } from 'lucide-react';
import { VendorAlias, UpdateVendorAliasRequest, StandardAccount, VendorAccountData } from '../../../api/types';
import vendorApi from '../../../api/vendorApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import StandardAccountSelector from '../components/StandardAccountSelector';
import QBOAccountSelector from '../components/QBOAccountSelector';
import { QboAccount } from '../../../api/types';

interface VendorAliasModalProps {
    isOpen: boolean;
    onClose: () => void;
    rawNormName: string;
    direction: string;
    businessId?: string;
    transactionId?: string | null;
    initialCleanVendorName?: string;
    autoStartEditing?: boolean;
}

const VendorAliasModal: React.FC<VendorAliasModalProps> = ({
    isOpen,
    onClose,
    rawNormName,
    direction,
    businessId,
    transactionId,
    initialCleanVendorName,
    autoStartEditing = false,
}) => {
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState(false);
    const [isQBOAccountSelectorOpen, setIsQBOAccountSelectorOpen] = useState(false);
    const [editData, setEditData] = useState<Partial<VendorAlias>>({});

    // Fetch alias by raw_norm_name and direction
    const { data: alias, isLoading } = useQuery({
        queryKey: ['vendorAlias', rawNormName, direction, businessId],
        queryFn: () => vendorApi.getAliasByLookup(rawNormName, direction, businessId),
        enabled: isOpen && !!rawNormName && !!direction,
        retry: false,
    });

    useEffect(() => {
        if (alias) {
            setEditData({
                clean_vendor_name: alias.clean_vendor_name,
                resolution_status: alias.resolution_status,
                finz_data: alias.finz_data,
                qbo_data: alias.qbo_data,
            });
        }
    }, [alias]);

    useEffect(() => {
        if (!isOpen) return;
        if (alias) return;
        setEditData((prev) => ({
            ...prev,
            clean_vendor_name: initialCleanVendorName || prev.clean_vendor_name || '',
            resolution_status: prev.resolution_status || 'verified',
        }));
        if (autoStartEditing) {
            setIsEditing(true);
        }
    }, [alias, autoStartEditing, initialCleanVendorName, isOpen]);

    const handleAccountSelect = (account: StandardAccount) => {
        const displayName = account.display_name || account.account_name;
        const newFinzData: VendorAccountData = {
            account_id: account._id,
            account_number: account.account_number,
            account_name: displayName,
            fiscal_bucket: account.fiscal_bucket,
        };

        setEditData(prev => ({
            ...prev,
            finz_data: newFinzData,
            resolution_status: 'verified',
        }));
    };

    const handleQBOAccountSelect = (account: QboAccount) => {
        const newQBOData: VendorAccountData = {
            account_id: account.data.account_id,
            account_name: account.data.fully_qualified_name || account.data.name,
            fiscal_bucket: account.data.account_type, // Using account_type as loosely fiscal bucket context
        };

        setEditData(prev => ({
            ...prev,
            qbo_data: newQBOData,
        }));
    };

    const updatemutation = useMutation({
        mutationFn: (data: UpdateVendorAliasRequest) => vendorApi.updateAlias(alias!._id, data, businessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vendorAliases'] });
            queryClient.invalidateQueries({ queryKey: ['vendorAlias', rawNormName, direction, businessId] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['transaction'] });
            if (transactionId) {
                queryClient.invalidateQueries({ queryKey: ['transaction', transactionId, businessId] });
            }
            setIsEditing(false);
        },
    });

    const createMutation = useMutation({
        mutationFn: (payload: { clean_vendor_name: string; resolution_status?: string; finz_data?: VendorAccountData; qbo_data?: VendorAccountData }) =>
            vendorApi.createAlias({
                raw_norm_name: rawNormName,
                clean_vendor_name: payload.clean_vendor_name,
                direction,
                finz_data: payload.finz_data,
                qbo_data: payload.qbo_data,
                is_user_verified: true,
            }, businessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vendorAliases'] });
            queryClient.invalidateQueries({ queryKey: ['vendorAlias', rawNormName, direction, businessId] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['transaction'] });
            if (transactionId) {
                queryClient.invalidateQueries({ queryKey: ['transaction', transactionId, businessId] });
            }
            setIsEditing(false);
            onClose();
        },
    });

    const handleSave = () => {
        if (alias?._id) {
            const payload: UpdateVendorAliasRequest = {
                clean_vendor_name: editData.clean_vendor_name,
                resolution_status: editData.resolution_status,
            };

            if (editData.finz_data) {
                payload.finz_data = editData.finz_data;
            }
            if (editData.qbo_data) {
                payload.qbo_data = editData.qbo_data;
            }

            updatemutation.mutate(payload);
            return;
        }

        if (!editData.clean_vendor_name?.trim()) return;

        createMutation.mutate({
            clean_vendor_name: editData.clean_vendor_name.trim(),
            resolution_status: editData.resolution_status,
            finz_data: editData.finz_data,
            qbo_data: editData.qbo_data,
        });
    };

    const handleCancel = () => {
        if (alias) {
            setEditData({
                clean_vendor_name: alias.clean_vendor_name,
                resolution_status: alias.resolution_status,
                finz_data: alias.finz_data,
                qbo_data: alias.qbo_data,
            });
        } else {
            setEditData({
                clean_vendor_name: initialCleanVendorName || '',
                resolution_status: 'verified',
            });
        }
        setIsEditing(false);
    };

    const isCreateMode = !alias;
    const savePending = updatemutation.isPending || createMutation.isPending;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[90] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    {isLoading ? (
                        <div className="p-6 text-center text-gray-500">Loading vendor details...</div>
                    ) : (
                        <>
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <Building2 className="h-6 w-6 text-indigo-600" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                        <div className="flex justify-between items-center">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                {isCreateMode ? 'Create Vendor Mapping' : 'Vendor Alias Details'}
                                            </h3>
                                            {!isEditing && !isCreateMode && (
                                                <button
                                                    onClick={() => setIsEditing(true)}
                                                    className="text-sm text-indigo-600 hover:text-indigo-900 font-medium"
                                                >
                                                    Edit
                                                </button>
                                            )}
                                        </div>

                                        <div className="mt-4 space-y-4">
                                            {isCreateMode && (
                                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                                    <div className="flex items-start gap-2">
                                                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                                        <p>
                                                            No vendor alias exists yet for "{rawNormName}" / "{direction}". Save here to create one and attach your mapping.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                            {/* Clean Vendor Name */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Clean Vendor Name</label>
                                                {isEditing || isCreateMode ? (
                                                    <input
                                                        type="text"
                                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        value={editData.clean_vendor_name || ''}
                                                        onChange={(e) => setEditData({ ...editData, clean_vendor_name: e.target.value })}
                                                    />
                                                ) : (
                                                    <p className="text-sm text-gray-900 mt-1 font-semibold">{alias?.clean_vendor_name}</p>
                                                )}
                                            </div>

                                            {/* Resolution Status */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Status</label>
                                                {isEditing || isCreateMode ? (
                                                    <select
                                                        value={editData.resolution_status || 'needs_review'}
                                                        onChange={(e) => setEditData(prev => ({ ...prev, resolution_status: e.target.value }))}
                                                        className="w-full appearance-none block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                    >
                                                        <option value="needs_review">Needs Review</option>
                                                        <option value="verified">Verified</option>
                                                        <option value="awaiting_approval">Awaiting Approval</option>
                                                        <option value="duplicated">Duplicated</option>
                                                    </select>
                                                ) : (
                                                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${(isEditing ? editData.resolution_status : alias.resolution_status) === 'verified'
                                                        ? 'bg-green-50 border-green-200 text-green-700'
                                                        : (isEditing ? editData.resolution_status : alias.resolution_status) === 'duplicated'
                                                            ? 'bg-rose-50 border-rose-200 text-rose-700'
                                                        : (isEditing ? editData.resolution_status : alias.resolution_status) === 'needs_review'
                                                            ? 'bg-red-50 border-red-200 text-red-700'
                                                            : 'bg-orange-50 border-orange-200 text-orange-700'
                                                        }`}
                                                    >
                                                        <CheckCircle size={16} />
                                                        <span className="capitalize">
                                                            {(isEditing ? editData.resolution_status : alias.resolution_status)?.replace(/_/g, ' ') || 'Needs Review'}
                                                        </span>
                                                    </div>
                                                )}
                                                {(isEditing || isCreateMode) && editData.resolution_status === 'duplicated' && (
                                                    <p className="mt-2 text-xs text-rose-600">
                                                        Saving this will remove matching transactions from `bank_normalized` and preserve them in raw data with `status=duplicated`.
                                                    </p>
                                                )}
                                            </div>

                                            {/* Raw Name & Direction */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500">Raw Normalized Name</label>
                                                    <p className="text-xs text-gray-600 font-mono bg-gray-50 p-1 rounded mt-1">{alias?.raw_norm_name || rawNormName}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500">Direction</label>
                                                    <p className="text-xs text-gray-600 font-mono bg-gray-50 p-1 rounded mt-1 capitalize">{alias?.direction || direction}</p>
                                                </div>
                                            </div>

                                            {/* QBO Status */}
                                            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                                                <div>
                                                    <span className="text-sm font-medium text-gray-700">QBO Registered</span>
                                                    {alias?.qbo_vendor_id && <span className="text-xs text-gray-400 block">ID: {alias.qbo_vendor_id}</span>}
                                                </div>
                                                {alias?.qbo_registered ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <CheckCircle className="w-3 h-3 mr-1" />
                                                        Yes
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                        No
                                                    </span>
                                                )}
                                            </div>

                                            {/* Finz Account Mapping */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Finz Account Mapping</label>
                                                {isEditing || isCreateMode ? (
                                                    <div className="space-y-2">
                                                        {editData.finz_data ? (
                                                            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg flex justify-between items-center">
                                                                <div>
                                                                    <p className="text-sm font-medium text-indigo-900">
                                                                        {editData.finz_data.account_name}
                                                                    </p>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-xs text-indigo-600">{editData.finz_data.fiscal_bucket}</span>
                                                                        <span className="text-xs text-indigo-400 font-mono mt-0.5">{editData.finz_data.account_number}</span>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => setIsAccountSelectorOpen(true)}
                                                                    className="text-xs font-medium text-indigo-700 hover:text-indigo-900 underline"
                                                                >
                                                                    Change
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => setIsAccountSelectorOpen(true)}
                                                                className="w-full flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-500 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                                                            >
                                                                Select Account Mapping
                                                            </button>
                                                        )}

                                                        <StandardAccountSelector
                                                            isOpen={isAccountSelectorOpen}
                                                            onClose={() => setIsAccountSelectorOpen(false)}
                                                            currentAccountId={editData.finz_data?.account_id}
                                                            onSelect={handleAccountSelect}
                                                            businessId={businessId}
                                                        />
                                                    </div>
                                                ) : (
                                                    alias?.finz_data ? (
                                                        <div className="border border-gray-200 rounded-md p-3">
                                                            <span className="font-medium text-gray-900">{alias.finz_data.account_name}</span>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <span className="text-xs text-gray-500">{alias.finz_data.fiscal_bucket}</span>
                                                                <span className="text-xs text-gray-400 font-mono">{alias.finz_data.account_number || alias.finz_data.account_id}</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-500 italic">No Finz mapping found.</p>
                                                    )
                                                )}
                                            </div>

                                            {/* QBO Account Mapping */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">QBO Account Mapping</label>
                                                {isEditing || isCreateMode ? (
                                                    <div className="space-y-2">
                                                        {editData.qbo_data ? (
                                                            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg flex justify-between items-center">
                                                                <div>
                                                                    <p className="text-sm font-medium text-indigo-900">
                                                                        {editData.qbo_data.account_name || editData.qbo_data.name}
                                                                    </p>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-xs text-indigo-400 font-mono mt-0.5">
                                                                            {editData.qbo_data.account_id || editData.qbo_data.id}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => setIsQBOAccountSelectorOpen(true)}
                                                                    className="text-xs font-medium text-indigo-700 hover:text-indigo-900 underline"
                                                                >
                                                                    Change
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => setIsQBOAccountSelectorOpen(true)}
                                                                className="w-full flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-500 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                                                            >
                                                                Select QBO Mapping
                                                            </button>
                                                        )}

                                                        <QBOAccountSelector
                                                            isOpen={isQBOAccountSelectorOpen}
                                                            onClose={() => setIsQBOAccountSelectorOpen(false)}
                                                            currentAccountId={editData.qbo_data?.account_id || editData.qbo_data?.id}
                                                            onSelect={handleQBOAccountSelect}
                                                            businessId={businessId}
                                                        />
                                                    </div>
                                                ) : (
                                                    alias?.qbo_data ? (
                                                        <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                                                            <span className="font-medium text-gray-900">
                                                                {alias.qbo_data.account_name || alias.qbo_data.name}
                                                            </span>
                                                            <span className="text-xs text-gray-400 font-mono block mt-1">
                                                                {alias.qbo_data.account_id || alias.qbo_data.id}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-500 italic">No QBO mapping found.</p>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                {isEditing || isCreateMode ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleSave}
                                            disabled={savePending || !editData.clean_vendor_name?.trim()}
                                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                        >
                                            {savePending ? 'Saving...' : isCreateMode ? 'Create Mapping' : 'Save Changes'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={isCreateMode ? onClose : handleCancel}
                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        >
                                            {isCreateMode ? 'Close' : 'Cancel'}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    >
                                        Close
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VendorAliasModal;
